import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { message, Modal } from "antd";
import {
  getAllAdminProducts,
  handleActive,
  selectProducts,
  selectCategories,
  menuCentralAdminThunk,
  disableMenuItemAdminThunk,
  importItemsThunk,
  getSyncStatusThunk,
  toggleIgnoreBranchOverridesThunk,
  selectChannels
} from "../../redux/slices/products";
import { selectUser } from "../../redux/slices/user";
import ProductsAdminUI from "./components/ProductsAdminUI";
import ProductsBranchUI from "./components/ProductsBranchUI";
import { getProductMenu, getProductSteps, updateProductStepsOrder, getProductCustomizableChannels } from "../../api/Products";

const Products = () => {
  const [loading, handleLoading] = useState(true);
  const products = useSelector(selectProducts);
  const categories = useSelector(selectCategories);
  const channels = useSelector(selectChannels);
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const [visibleEditor, handleVisibleEditor] = useState(false);

  // States for customizable channels config (for non-admins in branch view)
  const [customizableChannels, setCustomizableChannels] = useState([]);
  const [loadingCustomizable, setLoadingCustomizable] = useState(false);
  const [savingCustomizable] = useState(false);

  const fetchCustomizableChannelsList = useCallback(async (branchId) => {
    setLoadingCustomizable(true);
    const targetBranch = branchId || user.branch;
    if (targetBranch) {
      const response = await getProductCustomizableChannels(targetBranch);
      if (response && response.data && response.data.status === "success") {
        setCustomizableChannels(response.data.channels || []);
      } else {
        message.error("Error al cargar la configuración de canales");
      }
    }
    setLoadingCustomizable(false);
  }, [user.branch]);

  // Sync Global State
  const [isSyncing, setIsSyncing] = useState(false);
  const [localSyncing, setLocalSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [lastSyncStatus, setLastSyncStatus] = useState(null);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncOptions, setSyncOptions] = useState({
    categories: false,
    products: false,
    setmenu: false,
    condiments: false,
    sku: ""
  });

  const initialFetch = useCallback(async () => {
    handleLoading(true);

    const response = await dispatch(user.admin ? menuCentralAdminThunk(user.id) : getAllAdminProducts(user.branch));

    if (response.status !== "success") {
      message.error("¡Hubo un problema!");
    }

    if (!user.admin && user.branch) {
      await fetchCustomizableChannelsList(user.branch);
    }

    handleLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user]);

  useEffect(() => {
    initialFetch();
  }, [initialFetch]);

  // Polling para revisar el estado global de la sincronización
  useEffect(() => {
    let interval = null;

    const fetchSyncStatus = async () => {
      const response = await dispatch(getSyncStatusThunk());
      if (response.status === "success" && response.data) {
        if (!localSyncing) {
          setIsSyncing(response.data.isSyncing);
        }
        if (response.data.lastSyncTime) setLastSyncTime(response.data.lastSyncTime);
        if (response.data.lastSyncStatus) setLastSyncStatus(response.data.lastSyncStatus);
      }
    };

    fetchSyncStatus(); // llamada inicial
    interval = setInterval(fetchSyncStatus, 5000); // Polling cada 5 segundos

    return () => clearInterval(interval);
  }, [dispatch, localSyncing]);

  const handleProductActive = async (idProduct, val, type, branch, userId, entityType) => {
    const response = await dispatch(handleActive(idProduct, val, type, branch, userId, entityType));
    if (response.status !== "success") {
      message.error("¡Hubo un problema!");
    }
  };

  const handleToggleIgnoreOverrides = async (sku) => {
    const response = await dispatch(toggleIgnoreBranchOverridesThunk(user.id, sku));
    if (response.status !== "success") {
      message.error("¡Hubo un problema al actualizar la configuración!");
    } else {
      message.success("Configuración de overrides actualizada.");
    }
  };

  const confirmVisibility = (sku, mode, active) => {
    Modal.confirm({
      title: `¿${active ? 'Desactivar' : 'Activar'} para todas las sucursales?`,
      okText: "Si",
      cancelText: "No",
      async onOk() {
        const response = await dispatch(disableMenuItemAdminThunk(user.id, sku, mode))
        if (response.status !== "success") {
          message.error("¡Hubo un problema!");
        }
        initialFetch();
      },
      onCancel() {

      },
    });
  }
  const closeEditor = () => {
    handleVisibleEditor(false)
  }
  const openEditor = () => {
    handleVisibleEditor(true)
  }
  const handleSyncSubmit = async () => {
    const selectedTypes = [];
    if (syncOptions.categories) selectedTypes.push("categories");
    if (syncOptions.products) selectedTypes.push("products");
    if (syncOptions.setmenu) selectedTypes.push("setmenu");
    if (syncOptions.condiments) selectedTypes.push("condiments");

    const whatsync = selectedTypes.join(",");
    const sku = syncOptions.sku.trim() || undefined;

    if (!whatsync && !sku) {
      message.warning("Debes seleccionar al menos una opción para sincronizar.");
      return;
    }

    setLocalSyncing(true);
    setIsSyncing(true); // Actualizamos estado local inmediatamente para UX
    setSyncModalVisible(false);
    
    // Resetear formulario para futuras aperturas
    setSyncOptions({
      categories: false,
      products: false,
      setmenu: false,
      condiments: false,
      sku: ""
    });

    const response = await dispatch(importItemsThunk(whatsync, sku));
    if (response.status !== "success") {
      message.error("¡Hubo un problema al iniciar la sincronización!");
      setIsSyncing(false); // Revertir en caso de error inmediato
      setLocalSyncing(false);
    } else {
      message.success("Sincronización iniciada en background.");
      // Dejar que localSyncing se mantenga por 3 segundos para dar tiempo a que la DB registre el true
      setTimeout(() => {
        setLocalSyncing(false);
      }, 3000);
    }
  }

  return user.admin === 1 ? (
    <ProductsAdminUI
      loading={loading}
      products={products}
      initialFetch={initialFetch}
      handleProductActive={handleProductActive}
      categories={categories}
      confirmVisibility={confirmVisibility}
      visibleEditor={visibleEditor}
      closeEditor={closeEditor}
      openEditor={openEditor}
      syncModalVisible={syncModalVisible}
      setSyncModalVisible={setSyncModalVisible}
      isSyncing={isSyncing}
      lastSyncTime={lastSyncTime}
      lastSyncStatus={lastSyncStatus}
      syncOptions={syncOptions}
      setSyncOptions={setSyncOptions}
      handleSyncSubmit={handleSyncSubmit}
      handleToggleIgnoreOverrides={handleToggleIgnoreOverrides}
      getProductMenu={getProductMenu}
      getProductSteps={getProductSteps}
      updateProductStepsOrder={updateProductStepsOrder}
    />
  ) : (
    <ProductsBranchUI
      loading={loading}
      products={products}
      channels={channels}
      customizableChannels={customizableChannels}
      initialFetch={initialFetch}
      handleProductActive={handleProductActive}
      categories={categories}
      confirmVisibility={confirmVisibility}
      visibleEditor={visibleEditor}
      closeEditor={closeEditor}
      openEditor={openEditor}
      getProductMenu={getProductMenu}
      getProductSteps={getProductSteps}
      updateProductStepsOrder={updateProductStepsOrder}
      loadingCustomizable={loadingCustomizable}
      savingCustomizable={savingCustomizable}
      fetchCustomizableChannels={fetchCustomizableChannelsList}
    />
  );
};

export default Products;
